import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@/components/ui/select'
import { MultiSelect, type MultiSelectOption } from '@/components/ui/multi-select'
import { KEBAB_CASE_REGEX, WORKFLOW_DEFINITION_CATEGORIES, CATEGORY_LABELS, LINKED_ENTITY_SCOPES, LINKED_ENTITY_SCOPE_LABELS } from '@/types'
import type { WorkflowDefinitionCategory, LinkedEntityScope } from '@/types'

interface EditorFormFieldsProps {
  displayName: string
  internalName: string
  description: string
  version: string
  isActive: boolean
  permittedRoleIds: string[]
  roleOptions: MultiSelectOption[]
  category: WorkflowDefinitionCategory | undefined
  linkedEntityScope: LinkedEntityScope | undefined
  onDisplayNameChange: (value: string) => void
  onInternalNameChange: (value: string) => void
  onDescriptionChange: (value: string) => void
  onVersionChange: (value: string) => void
  onIsActiveChange: (value: boolean) => void
  onPermittedRoleIdsChange: (value: string[]) => void
  onCategoryChange: (value: WorkflowDefinitionCategory | undefined) => void
  onLinkedEntityScopeChange: (value: LinkedEntityScope | undefined) => void
  isReadOnly: boolean
  canChangeStatus: boolean
  attemptedSave: boolean
}

export function EditorFormFields({
  displayName,
  internalName,
  description,
  version,
  isActive,
  permittedRoleIds,
  roleOptions,
  category,
  linkedEntityScope,
  onDisplayNameChange,
  onInternalNameChange,
  onDescriptionChange,
  onVersionChange,
  onIsActiveChange,
  onPermittedRoleIdsChange,
  onCategoryChange,
  onLinkedEntityScopeChange,
  isReadOnly,
  canChangeStatus,
  attemptedSave,
}: EditorFormFieldsProps) {
  const displayNameError = attemptedSave && !displayName.trim() ? 'Display name is required' : undefined
  const internalNameError = attemptedSave && !internalName.trim()
    ? 'Internal name is required'
    : internalName.length > 0 && !KEBAB_CASE_REGEX.test(internalName)
      ? 'Must be kebab-case (e.g. "my-workflow-1")'
      : undefined
  const versionError = attemptedSave && !version.trim() ? 'Version is required' : undefined
  const permittedRolesError = attemptedSave && permittedRoleIds.length === 0 ? 'At least one role must be assigned' : undefined
  const hasAnyError = !!(displayNameError || internalNameError || versionError || permittedRolesError)

  return (
    <div className="space-y-5">
      <div className="flex items-end gap-6">
        <div className="flex-1 min-w-0 space-y-2">
          <Label htmlFor="displayName" className="text-xs font-bold leading-[1.6] tracking-[0.015em] text-foreground">
            Display Name <span className="text-red-500">*</span>
          </Label>
          <Input
            id="displayName"
            value={displayName}
            onChange={(e) => onDisplayNameChange(e.target.value)}
            placeholder="e.g. Implement Work Item"
            disabled={isReadOnly}
            maxLength={256}
            className={`h-10 rounded-lg ${displayNameError ? 'border-danger-foreground' : 'border-input'}`}
            data-testid="display-name-input"
          />
          {hasAnyError && (
            <div className="min-h-[20px]">
              {displayNameError && (
                <p className="text-xs text-red-500" role="alert">{displayNameError}</p>
              )}
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0 space-y-2">
          <Label htmlFor="internalName" className="text-xs font-bold leading-[1.6] tracking-[0.015em] text-foreground">
            Internal Name <span className="text-red-500">*</span>
          </Label>
          <Input
            id="internalName"
            value={internalName}
            onChange={(e) => onInternalNameChange(e.target.value)}
            placeholder="e.g. implement-work-item"
            disabled={isReadOnly}
            className={`h-10 rounded-lg ${internalNameError ? 'border-danger-foreground' : 'border-input'}`}
            maxLength={256}
            data-testid="internal-name-input"
          />
          {hasAnyError && (
            <div className="min-h-[20px]">
              {internalNameError && (
                <p className="text-xs text-red-500" role="alert">{internalNameError}</p>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="flex items-end gap-6">
        <div className="flex-1 min-w-0 space-y-2">
          <Label htmlFor="availableTo" className="text-xs font-bold leading-[1.6] tracking-[0.015em] text-foreground">
            Available To <span className="text-red-500">*</span>
          </Label>
          <MultiSelect
            id="availableTo"
            options={roleOptions}
            value={permittedRoleIds}
            onChange={onPermittedRoleIdsChange}
            placeholder="e.g. Developers, Guests, QAs, Project Owners"
            disabled={isReadOnly}
            displayMode="compact"
            className={`h-10 rounded-lg ${permittedRolesError ? 'border-danger-foreground' : 'border-input'}`}
            placeholderClassName="truncate"
          />
          {hasAnyError && (
            <div className="min-h-[20px]">
              {permittedRolesError && (
                <p className="text-xs text-red-500" role="alert">{permittedRolesError}</p>
              )}
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0 flex items-end gap-6">
          <div className="flex-1 space-y-2">
            <Label htmlFor="version" className="text-xs font-bold leading-[1.6] tracking-[0.015em] text-foreground">
              Version <span className="text-red-500">*</span>
            </Label>
            <Input
              id="version"
              value={version}
              onChange={(e) => onVersionChange(e.target.value)}
              placeholder="e.g. 1.0.0"
              disabled={isReadOnly}
              maxLength={50}
              className={`h-10 rounded-lg ${versionError ? 'border-danger-foreground' : 'border-input'}`}
              data-testid="version-input"
            />
            {hasAnyError && (
              <div className="min-h-[20px]">
                {versionError && (
                  <p className="text-xs text-red-500" role="alert">{versionError}</p>
                )}
              </div>
            )}
          </div>
          <div className="flex-1 space-y-2">
            <Label htmlFor="status" className="text-xs font-bold leading-[1.6] tracking-[0.015em] text-foreground">
              Status <span className="text-red-500">*</span>
            </Label>
            <Select
              value={isActive ? 'true' : 'false'}
              onValueChange={(v) => onIsActiveChange(v === 'true')}
              disabled={isReadOnly || !canChangeStatus}
            >
              <SelectTrigger id="status" className="h-10 rounded-lg border-input" data-testid="status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="true">Active</SelectItem>
                <SelectItem value="false">Inactive</SelectItem>
              </SelectContent>
            </Select>
            {hasAnyError && <div className="min-h-[20px]" />}
          </div>
        </div>
      </div>
      <div className="flex items-end gap-6">
        <div className="flex-1 min-w-0 space-y-2">
          <Label htmlFor="description" className="text-xs font-bold leading-[1.6] tracking-[0.015em] text-foreground">
            Description
          </Label>
          <Input
            id="description"
            value={description}
            onChange={(e) => onDescriptionChange(e.target.value)}
            placeholder="Short description of this workflow"
            disabled={isReadOnly}
            maxLength={2000}
            className="h-10 rounded-lg border-input"
            data-testid="description-input"
          />
        </div>
        <div className="w-[200px] space-y-2">
          <Label htmlFor="category" className="text-xs font-bold leading-[1.6] tracking-[0.015em] text-foreground">
            Category
          </Label>
          <Select
            value={category ?? 'none'}
            onValueChange={(v) => onCategoryChange(v === 'none' ? undefined : v as WorkflowDefinitionCategory)}
            disabled={isReadOnly}
          >
            <SelectTrigger id="category" className="h-10 rounded-lg border-input" data-testid="category-select">
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {WORKFLOW_DEFINITION_CATEGORIES.map((cat) => (
                <SelectItem key={cat} value={cat}>{CATEGORY_LABELS[cat]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-[200px] space-y-2">
          <Label htmlFor="linkedEntityScope" className="text-xs font-bold leading-[1.6] tracking-[0.015em] text-foreground">
            Linked To
          </Label>
          <Select
            value={linkedEntityScope ?? 'none'}
            onValueChange={(v) => onLinkedEntityScopeChange(v === 'none' ? undefined : v as LinkedEntityScope)}
            disabled={isReadOnly}
          >
            <SelectTrigger id="linkedEntityScope" className="h-10 rounded-lg border-input" data-testid="linked-entity-scope-select">
              <SelectValue placeholder="None" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {LINKED_ENTITY_SCOPES.map((scope) => (
                <SelectItem key={scope} value={scope}>{LINKED_ENTITY_SCOPE_LABELS[scope]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  )
}
